import {ReactElement} from 'react';
import {Link} from '@shopify/hydrogen';

import VectorIcon from '../VectorIcon';
import NewsSharedSidebar from './NewsSharedSidebar.client';
import {useSocialShare} from '../../hooks';
import ParsedNewsArticleContent from './ParsedNewsArticleContent.client';
import {NewsArticleDetailType} from '../../types';
import {formatPublishedAtDate, getNewsArticleCategoryLabel} from '../../utils';
import NewsPreviewTile from './NewsPreviewTile.client';
import {
  NewsArticlePlaceholderDesktopPNG,
  NewsArticlePlaceholderMobilePNG,
} from '../../assets/img';
import ImageContainer from '../ImageContainer.client';
import {Product} from '@shopify/hydrogen/storefront-api-types';
import RedirectNonRewardsMember from '../member/RedirectNonRewardsMember.client';
import {useQuery as useBackendQuery} from 'react-query';
import {getNewsArticleBySlug} from '../../backendApi';
import Skeleton from 'react-loading-skeleton';
import WidgetArea from '../WidgetArea.client';

interface NewsDetailProps {
  newsArticle: NewsArticleDetailType;
  embeddedProducts: ReadonlyArray<Product>;
}

export default function NewsDetail({
  newsArticle,
  embeddedProducts,
}: NewsDetailProps): ReactElement {
  const {
    data: fetchedNewsArticle,
    error,
    isLoading,
  } = useBackendQuery(
    ['newsArticle', newsArticle.slug],
    () => getNewsArticleBySlug(newsArticle.slug),
    {
      enabled: newsArticle.is_rewards_members_only,
    },
  );
  if (!newsArticle.is_rewards_members_only) {
    return (
      <_NewsDetail
        newsArticle={newsArticle}
        embeddedProducts={embeddedProducts}
      />
    );
  }

  if (isLoading) {
    return <NewsDetailSkeleton />;
  }
  if (error || !fetchedNewsArticle) {
    return <>Error retrieving article</>;
  }

  if (fetchedNewsArticle) {
    return (
      <RedirectNonRewardsMember>
        <_NewsDetail
          newsArticle={fetchedNewsArticle}
          embeddedProducts={embeddedProducts}
        />
      </RedirectNonRewardsMember>
    );
  }
}

function _NewsDetail({
  newsArticle,
  embeddedProducts,
}: NewsDetailProps & {showSkeleton?: boolean}): ReactElement {
  const {
    facebookShareUrl,
    twitterShareUrl,
    linkedinShareUrl,
    emailShareUrl,
    onShare,
  } = useSocialShare(`/news/${newsArticle?.slug}`, newsArticle?.title);

  const {
    title,
    category,
    published_at,
    featured_image_desktop_webp,
    featured_image_desktop_jpeg,
    featured_image_mobile_webp,
    featured_image_mobile_jpeg,
    sidebar_promotion,
    published_years,
    review_articles,
    associated_articles,
  } = newsArticle;

  return (
    <>
      <section>
        <div className="page-title container mb-0">
          <h1 className="title-0 page-title__heading">{title}</h1>
          <div className="page-title__meta">
            <div className="page-title__date date-element separator-r">
              <span>{formatPublishedAtDate(published_at)}</span>
            </div>
            <Link to={`/news/?category=${category}`} className="meta-link">
              {getNewsArticleCategoryLabel(category)}
            </Link>
          </div>
        </div>
      </section>
      <div className="breadcrumbs">
        <div className="container">
          <Link to="/" className="breadcrumbs-item">
            Home
          </Link>
          <Link to="/news" className="breadcrumbs-item">
            TWL News
          </Link>
          <Link
            to={`/news/?category=${category}`}
            className="breadcrumbs-item"
            prefetch={false}
          >
            &nbsp;{getNewsArticleCategoryLabel(category)}
          </Link>
        </div>
      </div>
      <section>
        <div className="hero-slider-outer hero-slider-only-image">
          <div className="hero-slider" id="hero-slider">
            <div className="hero-slide">
              <div className="hero-slide-d">
                <ImageContainer
                  jpegSrc={
                    featured_image_desktop_jpeg ||
                    NewsArticlePlaceholderDesktopPNG
                  }
                  webpSrc={featured_image_desktop_webp}
                  alt={title || 'News'}
                />
              </div>
              <div className="hero-slide-m">
                <ImageContainer
                  jpegSrc={
                    featured_image_mobile_jpeg ||
                    featured_image_desktop_jpeg ||
                    NewsArticlePlaceholderMobilePNG
                  }
                  webpSrc={featured_image_mobile_webp}
                  alt={title || 'News'}
                />
              </div>
            </div>
          </div>
        </div>
      </section>
      <section>
        <div className="news-detail has-widget-area container">
          <div className="has-widget-area__l">
            <ParsedNewsArticleContent
              newsArticle={newsArticle}
              embeddedProducts={embeddedProducts}
            />
            <div className="share-social-buttons">
              <span>Share this story</span>
              <div
                role="button"
                tabIndex={-1}
                onClick={() => onShare(facebookShareUrl)}
                className="share-social-buttons__item share-social-buttons__item-f"
              >
                <VectorIcon iconName="facebook-s" />
              </div>
              <div
                role="button"
                tabIndex={-1}
                onClick={() => onShare(twitterShareUrl)}
                className="share-social-buttons__item share-social-buttons__item-t"
              >
                <VectorIcon iconName="twitter-s" />
              </div>
              <div
                role="button"
                tabIndex={-1}
                onClick={() => onShare(linkedinShareUrl)}
                className="share-social-buttons__item share-social-buttons__item-l"
              >
                <VectorIcon iconName="linkedin-s" />
              </div>
              <div
                role="button"
                tabIndex={-1}
                onClick={() => onShare(emailShareUrl)}
                className="share-social-buttons__item share-social-buttons__item-e"
              >
                <VectorIcon iconName="email-s" />
              </div>
            </div>
          </div>
          <NewsSharedSidebar
            sidebarPromotion={sidebar_promotion}
            publishedYears={published_years}
            newsArticles={review_articles}
          />
        </div>
      </section>
      {associated_articles.length > 0 && (
        <>
          <div className="separator mt-75 mb-75"></div>
          <section>
            <div className="more-articles list-type-2 mt-75">
              <div className="container">
                <h5 className="title-1">In case you missed it...</h5>
                <div className="more-articles-list list-type-2__list image-hover">
                  {associated_articles.map((article) => (
                    <NewsPreviewTile key={article.slug} article={article} />
                  ))}
                </div>
                <div className="list-type-2__more">
                  <Link to="/news" className="button">
                    View all news
                  </Link>
                </div>
              </div>
            </div>
          </section>
        </>
      )}
    </>
  );
}

function NewsDetailSkeleton() {
  return (
    <>
      <section>
        <div className="page-title container mb-0">
          <h1 className="title-0 page-title__heading">
            <Skeleton />
          </h1>
          <div className="page-title__date date-element">
            <Skeleton width="100px" />
          </div>
        </div>
      </section>
      <div className="breadcrumbs">
        <Skeleton width="150px" />
      </div>
      <section>
        <div className="hero-slider-outer hero-slider-only-image">
          <div className="hero-slider">
            <div className="hero-slide">
              <Skeleton height="100%" />
            </div>
          </div>
        </div>
      </section>
      <section>
        <div className="news-detail has-widget-area container">
          <div className="has-widget-area__l">
            <div>
              <Skeleton count={4} />
              <Skeleton width="60%" />
            </div>
            <div className="mt-50">
              <Skeleton count={6} />
              <Skeleton width="80%" />
            </div>
            <div className="mt-50">
              <Skeleton count={5} />
              <Skeleton width="30%" />
            </div>
          </div>
          <WidgetArea title="Whisky News, Reviews &amp; More...">
            <div className="widgets-mobile-sticky" id="mobileWidgetsSticky">
              <Skeleton width="100%" height={400} />
            </div>
          </WidgetArea>
        </div>
      </section>
    </>
  );
}
